import { Injectable, signal } from '@angular/core';
import {
  AnonymizeRequest,
  MaximizerAiService,
  PIIContext,
} from '@maximizer/ai/data-access';
import { OpenAI } from '@maximizer/core/shared/domain';
import {
  CLIENT_MASK_NAMESPACE,
  createRedactionState,
  getRestrictedDataRedactionLabel,
  isRestrictedDataMaskType,
  namespaceClientMasks,
  redactRestrictedData,
} from '@maximizer/outlook/shared/domain';
import { Observable, map, of, tap } from 'rxjs';

@Injectable()
export class OutlookAiApiService {
  private readonly _maskToOriginal = signal(new Map<string, string>());
  readonly maskToOriginal = this._maskToOriginal.asReadonly();

  constructor(private readonly maximizerAi: MaximizerAiService) {}

  clear(): void {
    this._maskToOriginal.set(new Map());
  }

  deAnonymize(maskInner: string): string | undefined {
    const normalizedMask = maskInner.toUpperCase();
    return (
      this._maskToOriginal().get(`<${normalizedMask}>`) ??
      getRestrictedDataRedactionLabel(normalizedMask)
    );
  }

  deAnonymizeHtml(html: string): string {
    return html.replace(
      /(?:<|&lt;)([A-Z]+\d+)(?:>|&gt;)/g,
      (_, inner: string) => this.deAnonymize(inner) ?? `<${inner}>`,
    );
  }

  anonymizeMessages(
    messages: OpenAI.GPTMessage[],
    piiContext?: PIIContext,
    clearMappings = true,
  ): Observable<OpenAI.GPTMessage[]> {
    if (!messages?.length) return of(messages ?? []);
    if (clearMappings) this.clear();

    const redactionState = createRedactionState();
    const redactedMessages = messages.map((message) => ({
      ...message,
      content: redactRestrictedData(message.content, redactionState),
    }));

    const request: AnonymizeRequest = {
      PIIContext: piiContext?.contacts ?? [],
      Texts: redactedMessages.map((message) => message.content),
    };

    return this.maximizerAi.anonymize(request).pipe(
      tap((response) => {
        const next = new Map(this._maskToOriginal());
        (response.Data.Entities ?? [])
          .filter((entity) => !isRestrictedDataMaskType(entity.Type))
          .forEach((entity) =>
            next.set(
              `<${CLIENT_MASK_NAMESPACE}${entity.Type.toUpperCase()}>`,
              entity.Value,
            ),
          );
        this._maskToOriginal.set(next);
      }),
      map((response) => {
        const texts = response.Data.Texts;
        if (!texts?.length) return redactedMessages;
        return redactedMessages.map((message, i) => ({
          ...message,
          content:
            texts[i] != null ? namespaceClientMasks(texts[i]) : message.content,
        }));
      }),
    );
  }

  anonymizeContextAndUser(
    anonymized: boolean,
    contextMessages: OpenAI.GPTMessage[],
    userMessages: OpenAI.GPTMessage[],
    systemMessages: OpenAI.GPTMessage[],
    piiContext?: PIIContext,
  ): Observable<{
    context: OpenAI.GPTMessage[];
    user: OpenAI.GPTMessage[];
    system: OpenAI.GPTMessage[];
  }> {
    // Restricted identifiers are stripped from every message regardless of anonymizer setting
    const redactionState = createRedactionState();
    const redactMessages = (messages: OpenAI.GPTMessage[]) =>
      messages.map((message) => ({
        ...message,
        content: redactRestrictedData(message.content, redactionState),
      }));

    const redactedContext = redactMessages(contextMessages);
    const redactedUser = redactMessages(userMessages);
    const redactedSystem = redactMessages(systemMessages);

    if (!anonymized) {
      return of({
        context: redactedContext,
        user: redactedUser,
        system: redactedSystem,
      });
    }

    this.clear();
    const combined = [...redactedContext, ...redactedUser];

    return this.anonymizeMessages(combined, piiContext, false).pipe(
      map((anonymizedMessages) => ({
        context: anonymizedMessages.slice(0, redactedContext.length),
        user: anonymizedMessages.slice(redactedContext.length),
        system: redactedSystem,
      })),
    );
  }

  anonymizeByIdentities(
    text: string,
    identities: ReadonlyMap<string, string>,
  ): string {
    if (!identities?.size) return text;

    let result = text;
    for (const [mask, original] of identities.entries()) {
      if (!original) continue;
      result = result.replaceAll(
        OutlookAiApiService.wholeWordPattern(original),
        mask,
      );
    }
    return result;
  }

  private static wholeWordPattern(original: string): RegExp {
    const escaped = original.replaceAll(
      /[.*+?^${}()|[\]\\]/g,
      (character) => `\\${character}`,
    );
    return new RegExp(String.raw`\b${escaped}\b`, 'gi');
  }
}
